import React, { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useQueryClient, useMutation } from 'react-query';
import { supabase } from '../../lib/supabaseClient';

interface Region {
  id: string;
  code: string;
  name: string;
  currency_code: string;
  currency_symbol: string;
  exchange_rate: number;
}

interface Plan {
  id: string;
  name: string;
  price_usd: number;
}

interface RegionalPriceFormProps {
  region: Region;
  plan: Plan;
  currentPrice: number;
  onClose: () => void;
}

export const RegionalPriceForm: React.FC<RegionalPriceFormProps> = ({
  region,
  plan,
  currentPrice,
  onClose
}) => {
  const [price, setPrice] = useState((currentPrice / 100).toFixed(2));
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const { mutate: savePrice, isLoading } = useMutation(
    async () => {
      const priceInCents = Math.round(parseFloat(price) * 100);

      const { data: existing } = await supabase
        .from('regional_prices')
        .select('id')
        .eq('plan_id', plan.id)
        .eq('region_id', region.id)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('regional_prices')
          .update({ price: priceInCents })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('regional_prices')
          .insert([{
            plan_id: plan.id,
            region_id: region.id,
            price: priceInCents
          }]);
        if (error) throw error;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('regional-prices');
        onClose();
      },
      onError: (error: Error) => {
        setError(error.message);
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const priceValue = parseFloat(price);
    if (isNaN(priceValue) || priceValue <= 0) {
      setError('Price must be a positive number');
      return;
    }

    savePrice();
  };

  // Calculate suggested price based on exchange rate
  const suggestedPrice = (plan.price_usd / 100 / region.exchange_rate).toFixed(2);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={onClose}
        />
        
        <div className="inline-block w-full max-w-md my-8 text-left align-middle transition-all transform">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Edit Regional Price
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
                Set price for {plan.name} in {region.name} ({region.currency_code})
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Price in {region.currency_code}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                      {region.currency_symbol}
                    </span>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      step="0.01"
                      min="0"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Suggested price based on exchange rate: {region.currency_symbol}{suggestedPrice}
                  </p>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Price'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};